import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { HeaderBanner } from "../HeaderBanner";
import { CustomCheckbox } from "../CustomCheckbox";
import { Button } from "../Button";
import styles from "./SearchForm.module.scss";
import clsx from "clsx";
import { StatusPopup } from "../StatusPopup";
import SearchQuery from "../../../features/server-search/SearchQuery";
import { useDebounce } from "../../hooks/useDebounce";
import { useServersStore } from "../../store";
import { useProjectsStore } from "../../store/projectsStore.ts";
import { CustomRadio } from "../CustomRadio";

interface SearchFormProps {
  headerText: string;
  buttonText: string;
  popupTitles: {
    loadingServers: string;
    loadingSelect: string;
  };
  mode?: "server" | "project";
  onSubmitSuccess?: () => void;
}

export default function SearchForm({
  headerText,
  buttonText,
  popupTitles,
  mode = "server",
  onSubmitSuccess,
}: SearchFormProps) {
  const isProjectMode = mode === "project";

  const serversStore = useServersStore();
  const projectStore = useProjectsStore();

  const {
    servers,
    loading: serversLoading,
    error: serversError,
    selectedServer,
    serverStatus,
    selectLoading: serverSelectLoading,
    selectError: serverSelectError,
    checkServerStatus,
  } = serversStore;

  const {
    projects,
    selectedProjects,
    setSelectedProjects,
    recover,
    projectStatus,
    loading: projectsLoading,
    selectLoading: projectSelectLoading,
    selectError: projectSelectError,
    error: projectsError,
    backupDate,
    fetchBackupDate,
  } = projectStore;

  const [query, setQuery] = useState("");
  const [targetServerId, setTargetServerId] = useState<string | null>(null);
  const prevTargetServerId = useRef<string | null>(null);

  useEffect(() => {
    if (isProjectMode) {
      fetchBackupDate();
    }
  }, [isProjectMode, fetchBackupDate]);

  useEffect(() => {
    if (
      prevTargetServerId.current !== null &&
      prevTargetServerId.current !== targetServerId
    ) {
      projectStore.setSelectedProjects([]);
    }
    prevTargetServerId.current = targetServerId;
  }, [targetServerId, projectStore]);

  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();

  useEffect(() => {
    if (isProjectMode) {
      if (
        !projectSelectLoading &&
        projectSelectError === null &&
        projectStatus !== null
      ) {
        const target = servers.find((s) => s.id === targetServerId);
        if (target) {
          navigate(`/project/${target.alfan}`);
          onSubmitSuccess?.();
        }
      }
    } else {
      if (
        selectedServer &&
        !serverSelectLoading &&
        serverSelectError === null &&
        serverStatus !== null
      ) {
        navigate(`/status/${selectedServer.alfan}`);
        onSubmitSuccess?.();
      }
    }
  }, [
    isProjectMode,
    projectSelectLoading,
    projectSelectError,
    projectStatus,
    targetServerId,
    selectedServer,
    serverSelectLoading,
    serverSelectError,
    serverStatus,
    navigate,
    onSubmitSuccess,
    servers,
  ]);

  const filteredServers = useMemo(
    () =>
      isProjectMode
        ? servers
        : servers.filter((s) =>
            s.alfan.toLowerCase().includes(debouncedQuery.toLowerCase()),
          ),
    [servers, debouncedQuery, isProjectMode],
  );

  const filteredProjects = useMemo(() => {
    if (!targetServerId) return [];

    const selectedServer = servers.find((s) => s.id === targetServerId);
    const selectedAlfan = selectedServer?.alfan;

    if (!selectedAlfan) return [];

    return projects.filter(
      (p) =>
        p.alfan === selectedAlfan &&
        p.initial.toLowerCase().includes(debouncedQuery.toLowerCase()),
    );
  }, [targetServerId, servers, projects, debouncedQuery]);

  const allProjectsSelected = useMemo(
    () =>
      filteredProjects.length > 0 &&
      filteredProjects.every((p) => selectedProjects.includes(p.initial)),
    [filteredProjects, selectedProjects],
  );

  const handleSelectAll = () => {
    if (filteredProjects.length === 0) return;

    if (allProjectsSelected) {
      setSelectedProjects([]);
    } else {
      const allInitials = filteredProjects.map((p) => p.initial);
      setSelectedProjects(allInitials);
    }
  };

  const selectedLabel = targetServerId
    ? (servers.find((s) => s.id === targetServerId)?.alfan ??
      "Выберите сервер проекта...")
    : "Выберите сервер проекта...";

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSubmit = async () => {
    if (isProjectMode) {
      if (!targetServerId || selectedProjects.length === 0) return;

      const target = servers.find((s) => s.id === targetServerId);
      if (!target) return;

      try {
        projectStore.clearRecovery();
        await projectStore.recover(target.alfan, selectedProjects, true, false);
      } catch (err) {
        console.warn("Recover failed", err);
      }
    } else {
      if (!targetServerId) return;

      try {
        await serversStore.checkServerStatus(targetServerId, true);

        const server = servers.find((s) => s.id === targetServerId);
        if (server) {
          navigate(`/status/${server.alfan}`);
          onSubmitSuccess?.();
        }
      } catch (err) {
        console.warn("Check status failed", err);
      }
    }
  };

  const handleRetry = () => {
    if (isProjectMode) {
      if (projectSelectError && targetServerId && selectedProjects.length > 0) {
        const target = servers.find((s) => s.id === targetServerId);
        if (target) recover(target.alfan, selectedProjects, true, false);
      }
    } else if (targetServerId) {
      checkServerStatus(targetServerId, true);
    }
  };

  const loading = isProjectMode ? projectsLoading : serversLoading;
  const error = isProjectMode ? projectsError : serversError;
  const selectLoading = isProjectMode
    ? projectSelectLoading
    : serverSelectLoading;
  const selectError = isProjectMode ? projectSelectError : serverSelectError;

  const submitButtonText = isProjectMode
    ? selectedProjects.length === 1
      ? "Перенести проект"
      : "Перенести проекты"
    : buttonText;

  return (
    <>
      {(loading || error || selectLoading || selectError) && (
        <StatusPopup
          title={
            selectLoading
              ? popupTitles.loadingSelect
              : loading
                ? popupTitles.loadingServers
                : "Ошибка"
          }
          mode={selectLoading || loading ? "loading" : "error"}
          errorMessage={selectError || error || null}
          onRetry={loading || selectLoading ? undefined : handleRetry}
        />
      )}

      {!loading && !selectLoading && !error && !selectError && (
        <div className={styles.left}>
          <HeaderBanner type="wood">{headerText}</HeaderBanner>

          {!(isProjectMode && !targetServerId) && (
            <SearchQuery
              placeholder={isProjectMode ? "Поиск проектов" : "Поиск сервера"}
              searchQuery={query}
              onSearchQueryChange={setQuery}
              onClear={() => setQuery("")}
              isLoading={loading}
            />
          )}

          <form
            className={clsx(
              "form-frame",
              styles.form,
              isProjectMode ? styles.projectForm : styles.serverForm,
            )}
          >
            {isProjectMode ? (
              <>
                <div className={styles.targetSection}>
                  <p className={styles.message}>
                    Выберите сервер, чтобы увидеть лежащие на нем проекты
                  </p>
                  <div className={styles.servers}>
                    {filteredServers.length > 0 ? (
                      <div
                        ref={dropdownRef}
                        className={clsx(
                          styles.customServerDropdown,
                          isOpen && styles.open,
                        )}
                      >
                        <div
                          className={styles.trigger}
                          onClick={() => setIsOpen((prev) => !prev)}
                        >
                          <span className={styles.selectedValue}>
                            {selectedLabel}
                          </span>
                        </div>

                        <div className={styles.optionsList}>
                          {filteredServers.map((server) => (
                            <div
                              key={server.id}
                              className={clsx(
                                styles.option,
                                targetServerId === server.id && "selected",
                              )}
                              onClick={() => {
                                setTargetServerId(server.id);
                                setIsOpen(false);
                              }}
                            >
                              <span
                                className={clsx(styles.text, styles.optionText)}
                              >
                                {server.alfan}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p>Серверы не найдены</p>
                    )}
                  </div>
                </div>

                {targetServerId ? (
                  <div
                    className={styles.targetSection}
                    key={`section-${targetServerId}`}
                  >
                    <div className={styles.projectsHeader}>
                      <div>
                        <p className={styles.message}>Проекты для переноса</p>
                        {backupDate && (
                          <p className={styles.backupDate}>
                            Дата последнего бэкапа:{" "}
                            <strong>{backupDate}</strong>
                          </p>
                        )}
                      </div>

                      {filteredProjects.length > 0 && (
                        <span
                          className={styles.selectAll}
                          onClick={handleSelectAll}
                        >
                          {allProjectsSelected ? "Снять все" : "Выбрать все"}
                        </span>
                      )}
                    </div>

                    <div
                      className={styles.projects}
                      key={`projects-${targetServerId}`}
                    >
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((project) => (
                          <CustomCheckbox
                            key={project.initial}
                            label={project.initial}
                            value={project.initial}
                            checked={selectedProjects.includes(project.initial)}
                            onChange={(val, isChecked) => {
                              const newSelected = isChecked
                                ? [...selectedProjects, val]
                                : selectedProjects.filter((i) => i !== val);
                              setSelectedProjects(newSelected);
                            }}
                          />
                        ))
                      ) : (
                        <p className={styles.errorMessage}>
                          На этом сервере проектов по запросу не найдено.
                        </p>
                      )}
                    </div>
                  </div>
                ) : null}
              </>
            ) : !error && filteredServers.length > 0 ? (
              <div className={styles.servers}>
                {filteredServers.map((server) => (
                  <CustomRadio
                    key={server.id}
                    label={server.alfan}
                    value={server.id}
                    checked={targetServerId === server.id}
                    onChange={setTargetServerId}
                  />
                ))}
              </div>
            ) : (
              <p className={styles.errorMessage}>
                По вашему запросу ничего не найдено.
                <br /> Попробуйте другой поиск.
              </p>
            )}
            <Button
              variant="wood"
              className={styles.submitBtn}
              disabled={
                isProjectMode
                  ? !targetServerId ||
                    selectedProjects.length === 0 ||
                    loading ||
                    !!error ||
                    selectLoading
                  : !targetServerId ||
                    loading ||
                    !!error ||
                    filteredServers.length === 0 ||
                    selectLoading
              }
              onClick={handleSubmit}
            >
              {submitButtonText}
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
